// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/features/financeiro/types/index.ts
//  Tipos alinhados com os DTOs retornados pela API Udisys.
// ════════════════════════════════════════════════════════════════════════════

// ── Contas a Pagar ────────────────────────────────────────────────────────

export interface ContaPagar {
  id: number;
  fornecedorId: number;
  centroCustoId: number;
  contaContabilId: number;
  tipoDespesasId: number;
  grupoDespesasId: number;
  dtEmissao: string;
  dtVencimento: string;
  dtLancamento: string;
  dtUltimaAtualizacao: string;
  dtPagamento?: string;
  valorOriginal: number;
  percDesconto: number;
  valorDesconto: number;
  percAcrescimo: number;
  valorAcrescimo: number;
  valorTotal: number;
  valorSaldo: number;
  isPago: boolean;
  qtdParcelas: number;
  nroParcela: number;
  observacao?: string;
  tenantId: string;
}

/**
 * Lançamento de conta a pagar com N parcelas (paridade desktop, espelho do
 * Contas a Receber). `valorParcela` é o valor de UMA parcela — o título vale
 * `valorParcela × qtdParcelas`, sem divisão e sem sobra de centavos.
 */
export interface CriarContaPagarRequest {
  fornecedorId: number;
  centroCustoId: number;
  contaContabilId: number;
  tipoDespesasId: number;
  grupoDespesasId: number;
  dtEmissao: string;
  /** Vencimento da 1ª parcela; as demais saem de `diasParcelas`. */
  dtVencimento: string;
  valorParcela: number;
  qtdParcelas?: number;
  /** Deslocamentos em dias a partir do 1º vencimento: "7,14,28". Vazio → 30 em 30. */
  diasParcelas?: string;
  /** Mantém o dia do mês do 1º vencimento nas demais parcelas. */
  diaFixo?: boolean;
  numDocumento?: string;
  duplicata?: string;
  contrato?: string;
  lancamento?: string;
}

export type AtualizarContaPagarRequest = Partial<CriarContaPagarRequest>;

// ── Contas a Receber ──────────────────────────────────────────────────────

export interface ContaReceber {
  id: number;
  clienteId: number;
  operacaoId: number;
  lancamentoId: number;
  numDocumento?: string;
  dtEmissao: string;
  dtVencimento: string;
  dtLancamento: string;
  dtUltimaAtualizacao: string;
  dtRecebimento?: string;
  valorOriginal: number;
  valorReceber: number;
  valorDesconto: number;
  percDesconto: number;
  valorAcrescimo: number;
  percAcrescimo: number;
  valorNota: number;
  valorSaldo: number;
  valorPago: number;
  isPago: boolean;
  qtdParcelas: number;
  nroParcela: number;
  tenantId: string;
}

// ── Resumo Financeiro ─────────────────────────────────────────────────────

export interface ResumoFinanceiro {
  totalPagar: number;
  totalReceber: number;
  totalPago: number;
  totalRecebido: number;
  saldoPrevisto: number;
}

// ── Compatibilidade com os hooks existentes ───────────────────────────────
// Os hooks usam "status" para colorir as linhas — calculamos no frontend
export type StatusConta = 'pendente' | 'pago' | 'atrasado' | 'recebido';

export function calcularStatus(isPago: boolean, dtVencimento: string): StatusConta {
  if (isPago) return 'pago';
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  return new Date(dtVencimento) < hoje ? 'atrasado' : 'pendente';
}

// ── DTOs alinhados com API (ContasDtos.cs) ────────────────────────────────

export interface ContaPagarResumoDto {
  id: number;
  fornecedorId: number;
  /** Nome vindo de wrd_clientes; null quando o id não resolve (mostrar o código). */
  fornecedorNome: string | null;
  numDocumento: string | null;
  duplicata: string | null;
  lancamento: string | null;
  dtEmissao: string;
  dtVencimento: string;
  dtPagamento: string | null;
  valorOriginal: number;
  valorDesconto: number;
  valorAcrescimo: number;
  valorTotal: number;
  valorSaldo: number;
  valorPago: number | null;
  isPago: boolean;
  qtdParcelas: number;
  nroParcela: number;
  centroCustoId: number;
  contaContabilId: number;
  tipoDespesasId: number;
  grupoDespesasId: number;
  dtCriacao: string;
}

export interface ContaReceberResumoDto {
  id: number;
  clienteId: number;
  /** Nome vindo de wrd_clientes; null quando o id não resolve (mostrar o código). */
  clienteNome: string | null;
  operacaoId: number;
  numDocumento: string;
  duplicata: string | null;
  lancamento: string | null;
  dtEmissao: string;
  dtVencimento: string;
  dtPagamento: string | null;
  valorOriginal: number;
  valorReceber: number;
  valorDesconto: number;
  valorAcrescimo: number;
  valorSaldo: number;
  valorPago: number;
  isPago: boolean;
  qtdParcelas: number;
  nroParcela: number;
  diasAtraso: number;
  dtCriacao: string;
}

export interface BaixarContaPagarRequest {
  dtPagamento: string;
  valorPago: number;
  valorDesconto?: number;
  valorAcrescimo?: number;
  formaPagamentoId?: number;
  observacao?: string;
}

export interface BaixarContaReceberRequest {
  dtPagamento: string;
  valorPago: number;
  valorDesconto?: number;
  valorAcrescimo?: number;
  formaPagamentoId?: number;
  observacao?: string;
}

export interface BaixarLoteRequest {
  ids: number[];
  dtPagamento: string;
  formaPagamentoId?: number;
  observacao?: string;
}

export interface BaixaResultado {
  id: number;
  isPago: boolean;
  valorPago: number;
  valorSaldo: number;
  dtPagamento: string | null;
  mensagem: string;
}

// ── Contas a Pagar POR PARCELA (paridade desktop, espelho do Contas a Receber) ─
//
// `contas_pagar` é o TÍTULO (a obrigação) e `contas_pagarbaixa` é a PARCELA. A
// tela operacional responde "o que eu pago hoje", então lista e baixa POR
// PARCELA; quitar o título inteiro é outra operação.

/** Uma linha da grid: a parcela, com o contexto mínimo do título. */
export interface ContaPagarParcelaDto {
  /** Id da PARCELA — é por ele que se baixa. 0 quando a linha veio do cabeçalho. */
  id: number;
  /** Id do TÍTULO que agrupa a parcela. */
  tituloId: number;
  fornecedorId: number;
  fornecedorNome: string | null;
  numDocumento: string | null;
  duplicata: string | null;
  lancamento: string | null;
  nroParcela: number;
  qtdParcelas: number;
  dtEmissao: string;
  dtVencimento: string;
  dtPagamento: string | null;
  valorTotal: number;
  valorPago: number;
  valorDesconto: number;
  valorAcrescimo: number;
  saldo: number;
  isPago: boolean;
  tituloIsPago: boolean;
  tituloValorTotal: number;
  tituloSaldo: number;
  /** Título legado sem parcela: a linha veio do cabeçalho (`id` = 0). */
  semParcela: boolean;
}

/** Pagamento de UMA parcela. `valorPago` menor que o saldo = baixa parcial. */
export interface BaixarParcelaPagarRequest {
  dtPagamento: string;
  valorPago: number;
  valorDesconto?: number;
  valorAcrescimo?: number;
  formaPagamentoId?: number;
  caixaId?: number;
  contaBancariaId?: number;
  observacao?: string;
}

/** Baixa de várias PARCELAS de pagar (os ids são de parcela, não de título). */
export interface BaixarParcelasPagarLoteRequest {
  ids: number[];
  dtPagamento: string;
  formaPagamentoId?: number;
  caixaId?: number;
  contaBancariaId?: number;
  observacao?: string;
}

// ── Contas a Pagar — VISÃO GERENCIAL ──────────────────────────────────────
//
// A operacional responde "o que eu pago hoje"; a gerencial, "a quem eu devo e
// como está cada obrigação": fornecedor → títulos → parcelas.
//
// Sem "cancelado" deste lado: `contas_pagar` não tem a coluna (só o a receber
// tem). Em compensação filtra pelas dimensões de DESPESA e CENTRO DE CUSTO.

/** Situação de um título a pagar no recorte gerencial. */
export type SituacaoTituloPagar = 'todos' | 'aberto' | 'quitado';

/** Uma linha do sintético: quanto se deve a um fornecedor no recorte. */
export interface ContaPagarFornecedorResumoDto {
  fornecedorId: number;
  fornecedorNome: string | null;
  qtdTitulos: number;
  qtdParcelas: number;
  valorTotal: number;
  valorPago: number;
  saldo: number;
  /** Parte do saldo já vencida — é o que cobra atenção. */
  saldoVencido: number;
  qtdParcelasVencidas: number;
  diasMaiorAtraso: number;
  proximoVencimento: string | null;
}

/** Uma linha do analítico: como está UM título (total / pago / saldo). */
export interface ContaPagarTituloGerencialDto {
  id: number;
  fornecedorId: number;
  fornecedorNome: string | null;
  numDocumento: string | null;
  duplicata: string | null;
  lancamento: string | null;
  dtEmissao: string;
  dtPrimeiroVencimento: string | null;
  dtUltimoVencimento: string | null;
  qtdParcelas: number;
  qtdParcelasPagas: number;
  valorTotal: number;
  valorPago: number;
  saldo: number;
  saldoVencido: number;
  diasMaiorAtraso: number;
  /** "Quitado", "Parcial" ou "Aberto". */
  situacao: string;
}

/** Prazo médio de pagamento de um fornecedor. */
export interface PrazoMedioFornecedorDto {
  fornecedorId: number;
  fornecedorNome: string | null;
  prazoMedioDias: number;
  qtdParcelas: number;
  valorPago: number;
  valorMedio: number;
}

/** Prazo médio de pagamento consolidado + abertura por fornecedor. */
export interface PrazoMedioPagamentoDto {
  prazoMedioDias: number;
  qtdParcelas: number;
  valorPago: number;
  porFornecedor: PrazoMedioFornecedorDto[];
}

/** Filtros compartilhados pelas três visões gerenciais de Contas a Pagar. */
export interface FiltroGerencialPagar {
  pagina?: number;
  tamanhoPagina?: number;
  busca?: string;
  fornecedorId?: number;
  grupoDespesasId?: number;
  tipoDespesasId?: number;
  centroCustoId?: number;
  dtInicio?: string;
  dtFim?: string;
  /** Base do período: emissão, vencimento (padrão) ou pagamento. */
  baseData?: BaseDataFinanceira;
  situacao?: SituacaoTituloPagar;
  ordenarPor?: string;
  direcao?: DirecaoOrdenacao;
}

// ── Contas a Receber POR PARCELA (paridade desktop) ───────────────────────
//
// `contas_receber` é o TÍTULO (o acordo) e `contas_receber_analitica` é a
// PARCELA. A tela operacional responde "o que eu recebo hoje", então lista e
// baixa POR PARCELA; quitar o título inteiro é outra operação.

/** Uma linha da grid: a parcela, com o contexto mínimo do título. */
export interface ContaReceberParcelaDto {
  /** Id da PARCELA — é por ele que se baixa. 0 quando a linha veio do cabeçalho. */
  id: number;
  /** Id do TÍTULO que agrupa a parcela. */
  tituloId: number;
  clienteId: number;
  clienteNome: string | null;
  numDocumento: string | null;
  duplicata: string | null;
  lancamento: string | null;
  nroParcela: number;
  qtdParcelas: number;
  dtEmissao: string;
  dtVencimento: string;
  dtPagamento: string | null;
  valorReceber: number;
  valorPago: number;
  valorDesconto: number;
  valorAcrescimo: number;
  saldo: number;
  isPago: boolean;
  formaPagamentoLancamento: string | null;
  tituloIsPago: boolean;
  tituloIsCancelado: boolean;
  tituloValorReceber: number;
  tituloSaldo: number;
  /** Título legado sem analítica: a linha veio do cabeçalho (`id` = 0). */
  semParcela: boolean;
  /**
   * A parcela já foi agrupada numa FATURA — a cobrança está sendo feita por ela.
   * Continua sendo a dívida (a fatura é só o documento), mas a tela avisa para
   * ninguém cobrar duas vezes.
   */
  isFaturada: boolean;
  /** Id da fatura que agrupa esta parcela (0 quando não faturada). */
  faturaId: number;
  /**
   * A parcela foi EXTINTA por uma renegociação — a dívida dela virou o título
   * novo. Não foi recebida: exibir "Recebido" aqui seria mentira.
   */
  isRenegociada: boolean;
  /** Id do título que substituiu esta parcela (0 quando não renegociada). */
  renegociadaEmTituloId: number;
}

/**
 * Lançamento com N parcelas. `valorParcela` é o valor de UMA parcela — o título
 * vale `valorParcela × qtdParcelas`, como no desktop. Não há divisão, logo não
 * há sobra de centavos.
 */
export interface CriarContaReceberRequest {
  clienteId: number;
  operacaoId?: number;
  lancamentoId?: number;
  dtEmissao: string;
  /** Vencimento da 1ª parcela; as demais saem de `diasParcelas`. */
  dtVencimento: string;
  valorParcela: number;
  qtdParcelas: number;
  /** Deslocamentos em dias a partir do 1º vencimento: "7,14,28". Vazio → 30 em 30. */
  diasParcelas?: string;
  /** Mantém o dia do mês do 1º vencimento nas demais parcelas. */
  diaFixo?: boolean;
  numDocumento?: string;
  duplicata?: string;
  contrato?: string;
  lancamento?: string;
  percDesconto?: number;
  percAcrescimo?: number;
  formaPagamentoId?: number;
  contaContabilId?: number;
  tipoDespesasId?: number;
}

export type AtualizarContaReceberRequest = Partial<CriarContaReceberRequest>;

/** Recebimento de UMA parcela. `valorPago` menor que o saldo = baixa parcial. */
export interface BaixarParcelaReceberRequest {
  dtPagamento: string;
  valorPago: number;
  valorDesconto?: number;
  valorAcrescimo?: number;
  formaPagamentoId?: number;
  caixaId?: number;
  contaBancariaId?: number;
  observacao?: string;
}

/** Baixa de várias PARCELAS (os ids são de parcela, não de título). */
export interface BaixarParcelasLoteRequest {
  ids: number[];
  dtPagamento: string;
  formaPagamentoId?: number;
  caixaId?: number;
  contaBancariaId?: number;
  observacao?: string;
}

// ── Contas a Receber — VISÃO GERENCIAL ────────────────────────────────────
//
// A operacional responde "o que eu recebo hoje"; a gerencial, "quem me deve e
// como está cada acordo": cliente → títulos → parcelas.

/**
 * Base do filtro de período — muda a PERGUNTA da tela.
 * `pagamento` só enxerga o que já foi recebido (parcela aberta não tem data de
 * pagamento), então combiná-lo com situação "aberto" devolve vazio por definição.
 */
export type BaseDataFinanceira = 'emissao' | 'vencimento' | 'pagamento';

export type SituacaoAcordo = 'todos' | 'aberto' | 'quitado' | 'cancelado';

/** Uma linha do sintético: quanto um cliente deve no recorte filtrado. */
export interface ContaReceberClienteResumoDto {
  clienteId: number;
  clienteNome: string | null;
  grupoClienteId: number | null;
  grupoClienteNome: string | null;
  qtdTitulos: number;
  qtdParcelas: number;
  valorTotal: number;
  valorRecebido: number;
  saldo: number;
  /** Parte do saldo já vencida — é o que cobra atenção. */
  saldoVencido: number;
  qtdParcelasVencidas: number;
  diasMaiorAtraso: number;
  proximoVencimento: string | null;
}

/** Uma linha do analítico: como está UM acordo. */
export interface ContaReceberTituloGerencialDto {
  id: number;
  clienteId: number;
  clienteNome: string | null;
  numDocumento: string | null;
  duplicata: string | null;
  lancamento: string | null;
  dtEmissao: string;
  dtPrimeiroVencimento: string | null;
  dtUltimoVencimento: string | null;
  qtdParcelas: number;
  qtdParcelasPagas: number;
  valorTotal: number;
  valorRecebido: number;
  saldo: number;
  saldoVencido: number;
  diasMaiorAtraso: number;
  /** "Cancelado" | "Quitado" | "Parcial" | "Aberto" */
  situacao: string;
  isCancelado: boolean;
}

export interface PrazoMedioClienteDto {
  clienteId: number;
  clienteNome: string | null;
  /** Média de dias entre EMISSÃO e RECEBIMENTO, como no desktop. */
  prazoMedioDias: number;
  qtdParcelas: number;
  valorRecebido: number;
  valorMedio: number;
}

export interface PrazoMedioRecebimentoDto {
  prazoMedioDias: number;
  qtdParcelas: number;
  valorRecebido: number;
  porCliente: PrazoMedioClienteDto[];
}

// ── Contas a Receber — FATURA ─────────────────────────────────────────────
//
// A fatura é um DOCUMENTO DE COBRANÇA que agrupa parcelas já existentes de um
// mesmo cliente numa data única. A DÍVIDA CONTINUA NOS TÍTULOS ORIGINAIS: por
// isso a fatura não aparece nas listagens de contas a receber (senão o total
// dobraria) e "baixar a fatura" significa baixar as parcelas que ela agrupa.

export interface ParcelaFaturavelDto {
  parcelaId: number;
  tituloId: number;
  numDocumento: string | null;
  nroParcela: number;
  qtdParcelas: number;
  dtEmissao: string;
  dtVencimento: string;
  saldo: number;
  diasAtraso: number;
}

export interface ClienteFaturavelDto {
  clienteId: number;
  clienteNome: string | null;
  grupoClienteId: number | null;
  grupoClienteNome: string | null;
  qtdParcelas: number;
  total: number;
  vencimentoMaisAntigo: string | null;
  parcelas: ParcelaFaturavelDto[];
}

/** Um cliente e as parcelas dele que entram na fatura. */
export interface GerarFaturaItem {
  clienteId: number;
  /** Ids de PARCELA (contas_receber_analitica), não de título. */
  parcelaIds: number[];
}

export interface GerarFaturasRequest {
  itens: GerarFaturaItem[];
  /** Vencimento aplicado a todas as faturas geradas nesta chamada. */
  dtVencimento: string;
  formaPagamentoId?: number;
  observacao?: string;
}

export interface FaturaResumoDto {
  id: number;
  clienteId: number;
  clienteNome: string | null;
  dtEmissao: string;
  dtVencimento: string;
  dtPagamento: string | null;
  valorTotal: number;
  valorPago: number;
  saldo: number;
  isPago: boolean;
  isCancelado: boolean;
  qtdTitulos: number;
  diasAtraso: number;
  /** "Cancelada" | "Recebida" | "Parcial" | "Vencida" | "Aberta" */
  situacao: string;
}

export interface FaturaDetalheDto {
  fatura: FaturaResumoDto;
  titulos: ParcelaFaturavelDto[];
}

export interface GerarFaturasResultadoDto {
  qtdFaturas: number;
  valorTotal: number;
  faturas: FaturaResumoDto[];
  avisos: string[];
}

export interface BaixarFaturaRequest {
  dtPagamento: string;
  formaPagamentoId?: number;
  caixaId?: number;
  contaBancariaId?: number;
  observacao?: string;
}

export interface FiltroFaturas {
  pagina?: number;
  tamanhoPagina?: number;
  busca?: string;
  clienteId?: number;
  dtInicio?: string;
  dtFim?: string;
  situacao?: 'todas' | 'aberta' | 'recebida' | 'vencida' | 'cancelada';
  /** Sem valor o servidor mantém a ordem antiga (nº da fatura, mais recente primeiro). */
  ordenarPor?: OrdenarPorFaturas;
  decrescente?: boolean;
}

export type OrdenarPorFaturas = 'vencimento' | 'emissao' | 'numero' | 'valor';

// ── Contas a Receber — RENEGOCIAÇÃO ───────────────────────────────────────
//
// O OPOSTO da fatura: renegociar é NOVAÇÃO. A dívida antiga é EXTINTA e
// substituída por um título novo, com novo valor e novo parcelamento. Os
// títulos antigos saem do a receber marcados como RENEGOCIADOS — e NÃO como
// pagos, porque nada foi recebido.

export interface RenegociarRequest {
  clienteId: number;
  /** Ids de PARCELA (contas_receber_analitica). */
  parcelaIds: number[];
  /** Valor de UMA parcela do novo acordo — acima do saldo antigo é juro. */
  valorParcela: number;
  qtdParcelas: number;
  /** Vencimento da 1ª parcela do acordo. */
  dtVencimento: string;
  diasParcelas?: string;
  diaFixo?: boolean;
  /** Número do documento que formalizou o acordo. */
  notaRenegociacao?: number;
  numDocumento?: string;
  observacao?: string;
  formaPagamentoId?: number;
}

export interface ParcelaRenegociadaDto {
  parcelaId: number;
  tituloId: number;
  numDocumento: string | null;
  nroParcela: number;
  qtdParcelas: number;
  dtVencimento: string;
  /** Saldo que a parcela tinha no momento da renegociação. */
  valorOriginal: number;
}

export interface RenegociacaoResumoDto {
  /** Id do título NOVO, que carrega a dívida agora. */
  id: number;
  clienteId: number;
  clienteNome: string | null;
  numDocumento: string | null;
  notaRenegociacao: number | null;
  dtRenegociacao: string;
  dtPrimeiroVencimento: string;
  qtdParcelas: number;
  /** Soma dos saldos que os títulos antigos tinham. */
  valorOriginal: number;
  /** Valor do novo acordo — a diferença é juro (ou desconto). */
  valorRenegociado: number;
  valorPago: number;
  saldo: number;
  isPago: boolean;
  isCancelado: boolean;
  qtdTitulosSubstituidos: number;
  /** "Cancelada" | "Quitado" | "Parcial" | "Em aberto" */
  situacao: string;
}

export interface RenegociacaoDetalheDto {
  acordo: RenegociacaoResumoDto;
  parcelasDoAcordo: ContaReceberAnaliticaDto[];
  titulosSubstituidos: ParcelaRenegociadaDto[];
}

/** Filtros das telas gerenciais (e da operacional, no caso de baseData). */
export interface FiltroGerencialReceber {
  pagina?: number;
  tamanhoPagina?: number;
  busca?: string;
  clienteId?: number;
  grupoClienteId?: number;
  dtInicio?: string;
  dtFim?: string;
  baseData?: BaseDataFinanceira;
  situacao?: SituacaoAcordo;
  ordenarPor?: string;
  direcao?: DirecaoOrdenacao;
}

export interface BaixaParcelaResultado {
  parcelaId: number;
  tituloId: number;
  parcelaQuitada: boolean;
  parcelaValorPago: number;
  parcelaSaldo: number;
  tituloQuitado: boolean;
  tituloSaldo: number;
  dtPagamento: string | null;
  mensagem: string;
}

// ── Paginação ─────────────────────────────────────────────────────────────
export interface PaginadoResponse<T> {
  itens: T[];
  total: number;
  pagina: number;
  tamanhoPagina: number;
  totalPaginas: number;
}

// ── Filtros + Totais de listagem (Contas a Pagar / Receber) ────────────────
export type StatusFiltro = 'todos' | 'pendente' | 'pago' | 'atrasado' | 'aberto';

/** Direção da ordenação da grid. */
export type DirecaoOrdenacao = 'asc' | 'desc';

/** Campos ordenáveis da grid de Contas a Pagar (espelham a whitelist do backend). */
export type OrdenarPorPagar =
  | 'id' | 'fornecedor' | 'documento' | 'parcela' | 'vencimento'
  | 'emissao' | 'valorTotal' | 'saldo' | 'status';

/** Campos ordenáveis da grid de Contas a Receber. */
export type OrdenarPorReceber =
  | 'id' | 'cliente' | 'documento' | 'parcela' | 'vencimento'
  | 'emissao' | 'valorReceber' | 'saldo' | 'status';

export interface FiltroContasPagar {
  pagina?: number;
  tamanhoPagina?: number;
  busca?: string;
  status?: StatusFiltro;
  dtInicio?: string;
  dtFim?: string;
  fornecedorId?: number;
  ordenarPor?: OrdenarPorPagar;
  direcao?: DirecaoOrdenacao;
  /** Inclui os títulos em aberto já vencidos antes do início do período (o atraso não some). */
  incluirVencidos?: boolean;
}

export interface FiltroContasReceber {
  pagina?: number;
  tamanhoPagina?: number;
  busca?: string;
  status?: StatusFiltro;
  dtInicio?: string;
  dtFim?: string;
  clienteId?: number;
  /** Base do período: emissão, vencimento (padrão) ou pagamento. */
  baseData?: BaseDataFinanceira;
  ordenarPor?: OrdenarPorReceber;
  direcao?: DirecaoOrdenacao;
  /** Inclui os títulos em aberto já vencidos antes do início do período (só com base no vencimento). */
  incluirVencidos?: boolean;
}

/** Totais sobre o conjunto filtrado INTEIRO (não a página) — alimenta os KPIs. */
export interface TotaisFinanceiros {
  totalEmAberto: number;
  totalQuitado: number;
  totalAtrasado: number;
  totalGeral: number;
  qtdTotal: number;
  qtdEmAberto: number;
  qtdQuitado: number;
  qtdAtrasado: number;
  proximoVencimento: string | null;
  proximoVencimentoValor: number | null;
}

export interface ListaFinanceiraResponse<T> {
  itens: T[];
  total: number;
  pagina: number;
  tamanhoPagina: number;
  totalPaginas: number;
  totais: TotaisFinanceiros;
}

// ── Banco ─────────────────────────────────────────────────────────────────
export interface Banco {
  id: number;
  codigoBanco: string;
  descricao: string;
}
export interface SalvarBancoRequest {
  codigoBanco: string;
  descricao: string;
}

// ── Conta Corrente ────────────────────────────────────────────────────────
export interface ContaCorrente {
  id: number;
  bancoId: number;
  descricao: string;
  numeroConta?: string;
  codigoAgencia?: string;
  carteira?: string;
  cedente?: string;
  convenio?: string;
  especieDocumento?: string;
  modalidade?: string;
  aceite: boolean;
  jurosMora?: number;
  taxaEmissaoBoleto?: number;
  multaPercentual?: number;
  multaValor?: number;
  descontoPercentual?: number;
  descontoValor?: number;
  limite?: number;
  saldoAtual?: number;
  nossoNumeroInicio?: number;
  nossoNumeroFim?: number;
  nossoNumeroAtual?: number;
  diaProtesto: number;
  descricaoBoleto?: string;
  localPagamento?: string;
  chavePix?: string;
  isAtivo: boolean;
  // Cobrança/CNAB extras
  diretorioRemessa?: string;
  diretorioRetorno?: string;
  nomeRemessa?: string;
  nomeRetorno?: string;
  layoutLote?: string;
  layoutArquivo?: string;
  bancoAcbrId?: number;
  digitoContaId?: number;
  digitoAgenciaId?: number;
  tipoIntegracao?: number;
  tipoCarteira?: number;
  tipoDocumento?: number;
  caracteristicaCobranca?: number;
  ofxPadraoInternacional?: boolean;
  numeroSequencial?: number;
  // PIX extras
  clienteIdPix?: string;
  clienteSecretPix?: string;
}
export interface SalvarContaCorrenteRequest {
  bancoId?: number;
  descricao: string;
  numeroConta?: string;
  codigoAgencia?: string;
  carteira?: string;
  cedente?: string;
  convenio?: string;
  especieDocumento?: string;
  modalidade?: string;
  aceite?: boolean;
  jurosMora?: number;
  taxaEmissaoBoleto?: number;
  multaPercentual?: number;
  multaValor?: number;
  descontoPercentual?: number;
  descontoValor?: number;
  limite?: number;
  saldoAtual?: number;
  nossoNumeroInicio?: number;
  nossoNumeroFim?: number;
  nossoNumeroAtual?: number;
  diasProtesto?: number;
  descricaoBoleto?: string;
  localPagamento?: string;
  chavePix?: string;
  isAtivo: boolean;
  // Cobrança/CNAB extras
  diretorioRemessa?: string;
  diretorioRetorno?: string;
  nomeRemessa?: string;
  nomeRetorno?: string;
  layoutLote?: string;
  layoutArquivo?: string;
  bancoAcbrId?: number;
  digitoContaId?: number;
  digitoAgenciaId?: number;
  tipoIntegracao?: number;
  tipoCarteira?: number;
  tipoDocumento?: number;
  caracteristicaCobranca?: number;
  ofxPadraoInternacional?: boolean;
  numeroSequencial?: number;
  // PIX extras
  clienteIdPix?: string;
  clienteSecretPix?: string;
}

/// Configuração de integração API (Inter / ACBr) por conta corrente
export interface ContaCorrenteIntegracao {
  contaCorrenteId: number;
  layoutCobrancaId?: number;
  layoutBoletoId?: number;
  instrucaoBancariaId?: number;
  bancoAcbrId?: number;
  tipoIntegracao?: number;          // 0=ACBr arquivo, 1=Inter API, 2=outro API
  tipoCarteira?: number;
  tipoDocumento?: number;
  caracteristicaCobranca?: number;
  codigoNegativacao?: number;
  tipoGeracaoBoleto?: number;
  ambiente?: number;                // 0=Homolog, 1=Produção
  operacao?: number;
  sslType?: number;
  tipoChavePix?: number;
  indicadorPix?: number;
  logRegistro?: number;
  useCertificateHttp?: number;
  qtdCopias?: number;
  imprimeMensagemPadrao: boolean;
  mostraPreview: boolean;
  removeAcento: boolean;
  lerCedente: boolean;
  clientId?: string;
  clientSecret?: string;
  keyUser?: string;
  scope?: string;
  pathGravarRegistro?: string;
  versaoDF?: string;
  localArquivoCrt?: string;
  localArquivoKey?: string;
  chavePix?: string;
}

export interface UploadCertificadoResultado {
  tipo: 'CRT' | 'KEY' | 'PFX';
  caminho: string;
  tamanho: number;
}

// ── Cartão ────────────────────────────────────────────────────────────────
export type TipoDebitoCredito = 0 | 1 | 2; // 0=Ambos, 1=Débito, 2=Crédito

export interface CartaoTaxa {
  id?: number;
  ateParcela: number;
  taxa: number;
}
export interface Cartao {
  id: number;
  nome: string;
  contaCorrenteId?: number;
  operadoraId?: number;
  credenciadoraId?: number;
  tipoDebitoCredito: TipoDebitoCredito;
  diaBasePagamento?: number;
  tipoDelivery?: number;
  numeroIdentificacao?: string;
  foneAtendimento?: string;
  taxaTef?: number;
  taxaAdicionalTef?: number;
  prazoPagamentoTef?: number;
  isVoucher: boolean;
  isCombustivel: boolean;
  isAtiva: boolean;
  tipoTefId?: number;
  permiteFidelidade?: number;
  taxaAntecipacaoTef?: number;
  taxas?: CartaoTaxa[];
}
export interface SalvarCartaoRequest {
  nome: string;
  contaCorrenteId?: number;
  operadoraId?: number;
  credenciadoraId?: number;
  tipoDebitoCredito: TipoDebitoCredito;
  diaBasePagamento?: number;
  tipoDelivery?: number;
  numeroIdentificacao?: string;
  foneAtendimento?: string;
  taxaTef?: number;
  taxaAdicionalTef?: number;
  prazoPagamentoTef?: number;
  isVoucher: boolean;
  isCombustivel: boolean;
  isAtiva: boolean;
  tipoTefId?: number;
  permiteFidelidade?: number;
  taxaAntecipacaoTef?: number;
  taxas?: CartaoTaxa[];
}

// ── Forma de Pagamento ────────────────────────────────────────────────────
export interface FormaPagamento {
  id: number;
  descricao?: string;
  isAtivo: boolean;
  permiteRecebimento: boolean;
  permitePagamento: boolean;
  vendaPrazo: boolean;
  geraBoleto: boolean;
  integradoTef: boolean;
  habilitaCashBox: boolean;
  isEntrada: boolean;
  isRetirada: boolean;
  isTroca: boolean;
  isTransferencia: boolean;
  permiteReforco: boolean;
  permiteSangria: boolean;
  teclaAtalho?: string;
  termoConfissaoDivida: boolean;
  compoemLimite: boolean;
  geraFatura: boolean;
  recebimentoAntecipado: boolean;
  vendaFutura: boolean;
  solicitaDocumento: boolean;
  trocoDinheiro: boolean;
  trocoCheque: boolean;
  consultaCheque: boolean;
  troco: boolean;
  acrescimo?: number;
  desconto?: number;
  perBaseTrocoMaximo?: number;
  geraComissao: boolean;
  horusPedido: boolean;
  horusServico: boolean;
  precoEspecialId?: number;
  tipoDelivery?: number;
  taxa?: number;
  gerarBoletoAutomatico: boolean;
  webTefMercadoPago: boolean;
  modalidadePrazoId?: number;
  operadoraCartaoId?: number;
  idPos?: string;
}
export interface SalvarFormaPagamentoRequest {
  descricao: string;
  isAtivo: boolean;
  // CashBox
  habilitaCashBox: boolean;
  isEntrada: boolean;
  isRetirada: boolean;
  isTroca: boolean;
  isTransferencia: boolean;
  permiteReforco: boolean;
  permiteSangria: boolean;
  teclaAtalho?: string;
  // Financeiro
  permiteRecebimento: boolean;
  permitePagamento: boolean;
  vendaPrazo: boolean;
  termoConfissaoDivida: boolean;
  compoemLimite: boolean;
  geraFatura: boolean;
  recebimentoAntecipado: boolean;
  vendaFutura: boolean;
  // Geral
  solicitaDocumento: boolean;
  trocoDinheiro: boolean;
  trocoCheque: boolean;
  consultaCheque: boolean;
  troco: boolean;
  acrescimo?: number;
  desconto?: number;
  perBaseTrocoMaximo?: number;
  // Pedidos
  geraComissao: boolean;
  horusPedido: boolean;
  horusServico: boolean;
  precoEspecialId?: number;
  tipoDelivery?: number;
  taxa?: number;
  // Banco/PIX/TEF
  geraBoleto: boolean;
  gerarBoletoAutomatico: boolean;
  integradoTef: boolean;
  webTefMercadoPago: boolean;
  modalidadePrazoId?: number;
  operadoraCartaoId?: number;
  idPos?: string;
}

// ── Condição de Pagamento ─────────────────────────────────────────────────
export interface CondicaoPagamentoParcela {
  id: number;
  numeroParcela: number;
  tipoData?: string;
  diaMes?: number;
  valorPercentual?: number;
  descontoPercentual: boolean;
  percentualDesconto?: number;
  descontoValor: boolean;
  valorDesconto?: number;
}

// Resumo usado na listagem
export interface CondicaoPagamento {
  id: number;
  formaPagRecId: number;
  codigoCondicao: number;
  descricao?: string;
  quantidadeParcelas?: number;
  diasFixo: boolean;
  isBloqueado: boolean;
}

// Detalhe completo usado no formulário de edição
export interface CondicaoPagamentoDetalhe {
  id: number;
  formaPagRecId: number;
  codigoCondicao: number;
  descricao?: string;
  tipoData?: string;
  tipoValor?: string;
  quantidadeParcelas?: number;
  diasMes?: number;
  foraMes: boolean;
  condicaoVencimento?: number;
  diasFixo: boolean;
  comissao: boolean;
  variacao?: number;
  perComissao?: number;
  isBloqueado: boolean;
  parcelas: CondicaoPagamentoParcela[];
}

export interface SalvarCondicaoPagamentoParcelaRequest {
  numeroParcela: number;
  tipoData?: string;
  diaMes?: number;
  valorPercentual?: number;
  descontoPercentual?: boolean;
  percentualDesconto?: number;
  descontoValor?: boolean;
  valorDesconto?: number;
}

export interface SalvarCondicaoPagamentoRequest {
  formaPagRecId: number;
  codigoCondicao?: number;
  descricao: string;
  tipoData?: string;
  tipoValor?: string;
  quantidadeParcelas?: number;
  diasMes?: number;
  foraMes?: boolean;
  condicaoVencimento?: number;
  diasFixo?: boolean;
  comissao?: boolean;
  variacao?: number;
  perComissao?: number;
  isBloqueado?: boolean;
  parcelas?: SalvarCondicaoPagamentoParcelaRequest[];
}

// ── Detalhe de Conta a Pagar ──────────────────────────────────────────────

export interface ContaPagarBaixaDto {
  id: number;
  dtPagamento: string | null;
  /** Vencimento da parcela — o dado relevante enquanto ela está em aberto. */
  dtVencimento: string;
  valorPago: number;
  valorDesconto: number;
  valorAcrescimo: number;
  valorTotal: number;
  saldo: number;
  nroParcela: number;
  isPago: boolean;
  formaPagRecId: number | null;
}

export interface ContaPagarDetalheDto {
  id: number;
  fornecedorId: number;
  /** Nome vindo de wrd_clientes; null quando o id não resolve (mostrar o código). */
  fornecedorNome: string | null;
  numDocumento: string | null;
  duplicata: string | null;
  contrato: string | null;
  lancamento: string | null;
  dtEmissao: string;
  dtVencimento: string;
  dtPagamento: string | null;
  valorOriginal: number;
  valorDesconto: number;
  valorAcrescimo: number;
  valorTotal: number;
  valorSaldo: number;
  valorPago: number | null;
  isPago: boolean;
  qtdParcelas: number;
  nroParcela: number;
  centroCustoId: number;
  contaContabilId: number;
  tipoDespesasId: number;
  grupoDespesasId: number;
  dtCriacao: string;
  baixas: ContaPagarBaixaDto[];
}

// ── Detalhe de Conta a Receber ────────────────────────────────────────────

export interface ContaReceberAnaliticaDto {
  id: number;
  nroParcela: number;
  dtPagamento: string | null;
  /** Vencimento da parcela — o dado relevante enquanto ela está em aberto. */
  dtVencimento: string;
  valorReceber: number;
  valorPago: number;
  valorDesconto: number;
  valorAcrescimo: number;
  saldo: number;
  isPago: boolean;
  formaPagamentoLancamento: string | null;
}

export interface ContaReceberDetalheDto {
  id: number;
  clienteId: number;
  /** Nome vindo de wrd_clientes; null quando o id não resolve (mostrar o código). */
  clienteNome: string | null;
  operacaoId: number;
  numDocumento: string;
  duplicata: string | null;
  contrato: string | null;
  lancamento: string | null;
  dtEmissao: string;
  dtVencimento: string;
  dtPagamento: string | null;
  valorOriginal: number;
  valorReceber: number;
  valorDesconto: number;
  valorAcrescimo: number;
  valorSaldo: number;
  valorPago: number;
  isPago: boolean;
  qtdParcelas: number;
  nroParcela: number;
  diasAtraso: number;
  isCancelado: boolean;
  dtCriacao: string;
  /** Classificação — para o form de edição pré-selecionar. 0/null = não classificado. */
  contaContabilId: number | null;
  tipoDespesasId: number | null;
  analiticas: ContaReceberAnaliticaDto[];
}

// ── Relatórios — Aging ────────────────────────────────────────────────────

export interface AgingFaixa {
  faixa: string;
  qtdContas: number;
  valorTotal: number;
}

export interface AgingRelatorio {
  dataBase: string;
  tipo: 'pagar' | 'receber';
  totalGeral: number;
  totalContas: number;
  faixas: AgingFaixa[];
}

// ── Relatórios — Inadimplência ────────────────────────────────────────────

export interface InadimplenciaItem {
  entidadeId: number;
  tipoEntidade: 'cliente' | 'fornecedor';
  qtdContas: number;
  valorTotal: number;
  diasMaximoAtraso: number;
  maiorContaVencida: number;
  /** Nome/CPF-CNPJ da pessoa (wrd_clientes). */
  nome?: string | null;
  cpfCnpj?: string | null;
}

export interface InadimplenciaRelatorio {
  dataBase: string;
  tipo: 'receber' | 'pagar';
  diasMinimos: number;
  totalInadimplente: number;
  totalContas: number;
  itens: InadimplenciaItem[];
}

// ── Conciliação ───────────────────────────────────────────────────────────

export interface ConciliacaoResultado {
  id: number;
  isConciliado: boolean;
  mensagem: string;
}

export interface ConciliarLoteRequest {
  ids: number[];
  conciliar: boolean;
}

// ── Boletos ───────────────────────────────────────────────────────────────

export interface BoletoResumo {
  id: number;
  clienteId: number;
  bancoId: number;
  dtVencimento: string;
  dtGeracao: string;
  dtEmissao: string | null;
  dtBaixa: string | null;
  dtCancelamento: string | null;
  valorTotal: number;
  valorRecebido: number;
  taxaBoleto: number;
  parcela: number;
  totalParcelas: number;
  nossoNumero: string | null;
  linhaDigitavel: string | null;
  codigoBarras: string | null;
  pixCopiaECola: string | null;
  pixTxid: string | null;
  observacao: string | null;
  isPago: boolean;
  isEstornado: boolean;
  isGeradoCnab: boolean;
  isImpresso: boolean;
  /// LOCAL = mov_boleto (desktop/CNAB); SICOOB = registrado pela API do banco.
  origem?: 'LOCAL' | 'SICOOB';
  contaCorrenteId?: number | null;
  /// Situação no banco (boletos por API): EM_ABERTO, LIQUIDADO, BAIXA_SOLICITADA, BAIXADO.
  situacao?: string | null;
  /** Nome do cliente (preenchido na listagem). */
  clienteNome?: string | null;
}

export interface EmitirBoletoRequest {
  clienteId: number;
  bancoId: number;
  dtVencimento: string;
  valorTotal: number;
  contasReceberBaixaId?: number;
  nossoNumero?: string;
  linhaDigitavel?: string;
  codigoBarras?: string;
  pixCopiaECola?: string;
  pixTxid?: string;
  taxaBoleto?: number;
  totalParcelas?: number;
  parcela?: number;
  diasProtesto?: number;
  diasDesconto?: number;
  isCobrarMulta?: boolean;
  multaPer?: number;
  multaValor?: number;
  jurosMora?: number;
  observacao?: string;
}

export interface BaixarBoletoRequest {
  dtBaixa: string;
  valorRecebido: number;
  valorDesconto?: number;
  valorAcrescimo?: number;
}

// ── Transferências entre Caixas ───────────────────────────────────────────

export interface RealizarTransferenciaRequest {
  caixaOrigemId: number;
  caixaDestinoId: number;
  valor: number;
  descricao: string;
  dtMovimento?: string;
  historico?: string;
}

export interface TransferenciaResultado {
  lancamentoDebitoId: number;
  lancamentoCreditoId: number;
  caixaOrigemId: number;
  caixaDestinoId: number;
  valor: number;
  descricao: string;
  dtMovimento: string;
  mensagem: string;
}

// ── Fluxo de Caixa / Previsão ─────────────────────────────────────────────

export interface FluxoCaixaPeriodo {
  label: string;
  dtInicio: string;
  dtFim: string;
  entradasRealizadas: number;
  saidasRealizadas: number;
  entradasPrevistas: number;
  saidasPrevistas: number;
  saldoPeriodo: number;
  saldoAcumulado: number;
}

export interface FluxoCaixa {
  dataInicio: string;
  dataFim: string;
  agrupamento: 'dia' | 'semana' | 'mes';
  totalEntradasRealizadas: number;
  totalSaidasRealizadas: number;
  totalEntradasPrevistas: number;
  totalSaidasPrevistas: number;
  saldoFinal: number;
  periodos: FluxoCaixaPeriodo[];
}

// ── Multi-Scenario Forecast ────────────────────────────────────────────────

export interface FluxoCaixaCenario {
  nome: string;
  ajustePerEntradas: number;
  ajusteDiasRecebimento: number;
  ajustePerSaidas: number;
  totalEntradasPrevistas: number;
  totalSaidasPrevistas: number;
  saldoFinal: number;
  saldoMinimo: number;
  saldoMinimoData: string;
  periodos: FluxoCaixaPeriodo[];
}

export interface FluxoCaixaCenarios {
  dataInicio: string;
  dataFim: string;
  agrupamento: 'dia' | 'semana' | 'mes';
  pessimista: FluxoCaixaCenario;
  realista: FluxoCaixaCenario;
  otimista: FluxoCaixaCenario;
}

export interface FluxoCaixaCenariosParams {
  dtInicio?: string;
  dtFim?: string;
  agrupamento?: 'dia' | 'semana' | 'mes';
  pessimistaPerEntradas?: number;
  pessimistaDiasAtraso?: number;
  pessimistaPerSaidas?: number;
  otimistaPerEntradas?: number;
  otimistaDiasAtraso?: number;
  otimistaPerSaidas?: number;
}

// ── Combos (lightweight) ──────────────────────────────────────────────────
export interface BancoCombo { id: number; codigoBanco: string; descricao: string; }
export interface ContaCorrenteCombo { id: number; descricao: string; bancoId?: number; }
export interface CartaoCombo { id: number; nome: string; tipoDebitoCredito: TipoDebitoCredito; }
export interface FormaPagamentoCombo { id: number; descricao: string; permiteRecebimento: boolean; permitePagamento: boolean; }
export interface CondicaoPagamentoCombo { id: number; formaPagRecId: number; codigoCondicao?: string; descricao?: string; quantidadeParcelas: number; diasMes?: number; diasFixo?: boolean; }
